import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // Institutional Editor: glassmorphism backdrop. Scrim flips per theme via --overlay.
      'fixed inset-0 z-[60] bg-[color:var(--overlay)] backdrop-blur-xl',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'responsive-full';
    overlayClassName?: string;
  }
>(({ className, children, showCloseButton = true, size = 'md', overlayClassName, ...props }, ref) => {
  const sizeClasses = {
    sm: 'max-w-sm max-h-[85vh]',
    md: 'max-w-lg max-h-[85vh]',
    lg: 'max-w-2xl max-h-[85vh]',
    xl: 'max-w-4xl max-h-[85vh]',
    full: 'max-w-none max-h-none w-screen h-screen',
    'responsive-full':
      'max-w-[95vw] max-h-[95vh] sm:max-w-[90vw] sm:max-h-[90vh] md:max-w-[85vw] md:max-h-[85vh] lg:max-w-[80vw] lg:max-h-[80vh] xl:max-w-[75vw] xl:max-h-[75vh] max-md:inset-0 max-md:rounded-none max-md:max-w-none max-md:max-h-none',
  };

  const isFullScreen = size === 'full';
  const isResponsiveFull = size === 'responsive-full';

  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // Institutional Editor: Glassmorphism panel
          'fixed z-[70] w-full overflow-hidden flex flex-col',
          'bg-surface-container-highest/95 backdrop-blur-xl',
          'shadow-[0_24px_48px_rgba(0,0,20,0.4),0_8px_16px_rgba(0,0,20,0.3)]',
          'duration-300',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          isFullScreen
            ? 'inset-0 rounded-none'
            : isResponsiveFull
              ? 'inset-2 sm:inset-4 md:inset-6 lg:inset-8 rounded-xl max-md:inset-0 max-md:rounded-none'
              : 'left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] rounded-xl data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
          sizeClasses[size],
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            className={cn(
              'absolute right-4 top-4 rounded-lg p-2',
              'text-text-muted hover:text-text-primary',
              'bg-surface-container hover:bg-surface-container-high',
              'transition-colors focus:outline-none focus:ring-2 focus:ring-accent-primary',
              'min-h-[44px] min-w-[44px] flex items-center justify-center'
            )}
            aria-label="Close"
            title="Close"
          >
            <X className="h-5 w-5" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 p-6 pb-4',
      // No-line rule: tonal shift instead of border
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
      'p-6 pt-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]',
      // No-line rule: tonal shift instead of border
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-xl font-display font-semibold leading-none tracking-tight',
      'text-text-primary',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-text-secondary', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex-1 overflow-auto p-6',
      'scrollbar-modern',
      className
    )}
    {...props}
  />
);
DialogBody.displayName = 'DialogBody';

/**
 * Wraps content so it is read by screen readers but not shown visually.
 * Use for accessible dialog titles when the visible header is not a single title (e.g. Chart modal).
 */
const VisuallyHidden = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('sr-only', className)} {...props} />
);
VisuallyHidden.displayName = 'VisuallyHidden';

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogBody,
  VisuallyHidden,
};
